import { useCallback, useState } from 'react';

import type { CSSProperties, PointerEvent } from 'react';

// Автовысота поля комментария: две строки по умолчанию, рост до четырёх,
// дальше собственный скролл textarea.
const COMMENT_AUTO_SIZE = { minRows: 2, maxRows: 4 } as const;
// Нативный уголок ресайза занимает правый нижний угол textarea.
const RESIZE_GRIP_SIZE = 18;

interface UseCommentFieldSizeResult {
  autoSize: typeof COMMENT_AUTO_SIZE | false;
  style?: CSSProperties;
  onPointerDown(event: PointerEvent<HTMLTextAreaElement>): void;
  onResize(size: { height: number }): void;
}

/**
 * Совмещает автовысоту Ant с ручным растягиванием за уголок.
 *
 * При включённом autoSize rc-textarea сам управляет высотой и возвращает её
 * к расчётной после любого ресайза, поэтому уголок выглядит рабочим, но
 * ничего не меняет. Как только пользователь берётся за уголок, автовысота
 * выключается, а высота фиксируется — дальше полем распоряжается только он.
 */
export const useCommentFieldSize = (): UseCommentFieldSizeResult => {
  const [manualHeight, setManualHeight] = useState<number | null>(null);

  const onPointerDown = useCallback(
    (event: PointerEvent<HTMLTextAreaElement>) => {
      if (manualHeight !== null) return;

      const { bottom, right, height } =
        event.currentTarget.getBoundingClientRect();
      const isGrip =
        right - event.clientX <= RESIZE_GRIP_SIZE &&
        bottom - event.clientY <= RESIZE_GRIP_SIZE;

      if (isGrip) {
        setManualHeight(height);
      }
    },
    [manualHeight]
  );

  // После выключения autoSize наблюдатель Ant остаётся включённым из-за
  // onResize и приносит высоту, которую пользователь выставил уголком, —
  // храним её, иначе перерисовка формы вернёт поле к высоте начала drag.
  const onResize = useCallback(({ height }: { height: number }) => {
    setManualHeight((previous) => (previous === null ? previous : height));
  }, []);

  return {
    autoSize: manualHeight === null ? COMMENT_AUTO_SIZE : false,
    style: manualHeight === null ? undefined : { height: manualHeight },
    onPointerDown,
    onResize,
  };
};
